import React, { useRef, useEffect } from 'react'
import { useMessaging } from './MessagingProvider'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import { cn } from '~/lib/utils'
import useTranslation from '../shared/TranslationProvider'
import { DeleteThreadDialog } from './DeleteThreadDialog'
import safeParse from '~/lib/safeParse'
import { useFormatTime } from '~/hooks/useFormatTime'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button } from '~/lib/ui/button'

const ThreadList = () => {
  const { t } = useTranslation('CoachMessageThread')
  const {
    location,
    currentThreadId,
    candidateSearch,
    newMessageMode,
    viewingSentMessages,
    isDesktop,
  } = useMessaging()
  const { error, data: messageThreads } = useQuery({
    queryKey: ['messageThreads', location.pathname],
    queryFn: () => apiGet(`${location.pathname}.json`).then((res) => res.data),
    refetchInterval: 10000,
  })

  const displayThreads = messageThreads?.filter(
    (thread) => !thread.coach_deleted_at && thread.has_incoming_messages,
  )
  const searchThreads = displayThreads?.filter((thread) =>
    (thread.company_name || '')
      .toLowerCase()
      .includes(candidateSearch.toLowerCase()),
  )
  const firstThreadId = searchThreads?.[0]?.id

  useEffect(() => {
    if (
      isDesktop &&
      !currentThreadId &&
      !newMessageMode &&
      !viewingSentMessages &&
      firstThreadId
    ) {
      window.location.hash = `#${firstThreadId}`
    }
  }, [
    firstThreadId,
    currentThreadId,
    isDesktop,
    newMessageMode,
    viewingSentMessages,
  ])

  if (error) return t('reply') + error.message

  if (currentThreadId && searchThreads?.length == 0) {
    window.location.href = '#inbox'
  }

  return (
    <div className="flex flex-col gap-2 p-4 pt-0">
      {searchThreads?.length === 0 && (
        <div className="flex h-full text-center text-sm">
          {t('received_messages_appear_here')}
        </div>
      )}
      {searchThreads?.map((thread) => (
        <ThreadButton key={thread.id} thread={thread} />
      ))}
    </div>
  )
}
export default ThreadList

const selectedRowLinkClass =
  '!text-intoo-text hover:!text-intoo-text focus:!text-intoo-text hover:!underline focus:!underline'

const ThreadButton = ({ thread }) => {
  const { t } = useTranslation('CoachMessageThread')
  const { currentThreadId } = useMessaging()
  const buttonRef = useRef(null)
  const {
    id: threadId,
    subject,
    last_message_created_at: lastMessageSentAt,
    last_message_preview: lastMessagePreview,
    candidate_name: candidateName,
    link_to_candidate: linkToCandidate,
    company_name: companyName,
  } = thread
  const isSelected = threadId === currentThreadId
  const formatTime = useFormatTime()

  const formattedLastMessageSentAt = formatTime(lastMessageSentAt, 'short')

  useEffect(() => {
    if (isSelected && buttonRef.current) {
      buttonRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isSelected, threadId, currentThreadId])

  return (
    <div
      ref={buttonRef}
      className={cn(
        'flex cursor-pointer flex-col items-start gap-2 rounded-3xl border border-silver px-6 py-4 text-left text-sm text-intoo-text transition-all hover:bg-intoo-blue-medium-50 hover:text-intoo-text focus:text-intoo-text',
        isSelected &&
          'border-none bg-intoo-blue-medium-100 text-intoo-blue-medium hover:bg-intoo-blue-medium-100/90 hover:text-intoo-blue-medium focus:text-intoo-blue-medium',
      )}
      role="link"
      tabIndex={0}
      onClick={() => (window.location.hash = `#${threadId}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          window.location.hash = `#${threadId}`
        }
      }}
    >
      <div className="mb-3 flex w-full flex-col gap-y-3">
        <div className="-mr-2 flex flex-row items-center justify-between">
          <div className="text-sm">
            {t('created_on')} {formattedLastMessageSentAt}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="icon"
              className="rounded-lg p-2 hover:bg-intoo-blue-medium/15 lg:hidden"
              aria-label={t('open_thread')}
              onClick={(e) => {
                e.stopPropagation()
                window.location.hash = `#${threadId}`
              }}
            >
              <PlusIcon className="h-5 text-intoo-blue-medium" />
            </Button>
            <DeleteThreadDialog threadId={threadId} />
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex items-center gap-2">
            <div className="text-lg">
              {t('from')}:{' '}
              <div>
                <a
                  href={linkToCandidate}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(isSelected && selectedRowLinkClass)}
                >
                  {candidateName}
                </a>
                <div className="text-sm text-mine-shaft-500">{companyName}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="font-bold">
          {t('subject')}: {subject}
        </div>
        <div className="line-clamp-2 [overflow-wrap:anywhere]">
          {safeParse(
            (lastMessagePreview || '')
              .replace(/&nbsp;/g, ' ')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&#39;|&amp;#39;/g, "'"),
          )}
        </div>
      </div>
    </div>
  )
}
